/** Fashion category label used across vendor deal forms */
export const FASHION_CATEGORY = "Fashion & Accessories";

/** Standard clothing sizes (alpha) */
export const CLOTHING_SIZES = ["XS", "S", "M", "L", "XL", "XXL", "3XL"];

/** Common EU shoe / numeric sizes */
export const NUMERIC_SIZES = [
  "35",
  "36",
  "37",
  "38",
  "39",
  "40",
  "41",
  "42",
  "43",
  "44",
  "45",
  "46",
];

/** Generic / accessory sizes */
export const GENERAL_SIZES = ["One Size", "Free Size", "Custom Fit"];

/**
 * Suggested presets by fashion subcategory.
 * Vendors can still pick from any group or add a custom size.
 */
export function getSuggestedSizeGroups(subcategory = "") {
  const sub = String(subcategory || "").toLowerCase();

  if (sub.includes("shoe")) {
    return [
      { id: "numeric", label: "Shoe sizes (EU)", options: NUMERIC_SIZES },
      { id: "clothing", label: "Clothing", options: CLOTHING_SIZES },
      { id: "general", label: "Other", options: GENERAL_SIZES },
    ];
  }

  if (sub.includes("bag") || sub.includes("jewel") || sub.includes("accessor")) {
    return [
      { id: "general", label: "Common", options: GENERAL_SIZES },
      { id: "clothing", label: "Clothing", options: CLOTHING_SIZES },
      { id: "numeric", label: "Numeric", options: NUMERIC_SIZES },
    ];
  }

  return [
    { id: "clothing", label: "Clothing", options: CLOTHING_SIZES },
    { id: "numeric", label: "Numeric / EU", options: NUMERIC_SIZES },
    { id: "general", label: "Other", options: GENERAL_SIZES },
  ];
}

/**
 * Category or subcategory labels that sell sized goods.
 * Matched on the name because the backend no longer has a single
 * "Fashion & Accessories" category.
 */
const SIZE_LABEL_PATTERN =
  /fashion|cloth(?:es|ing)?|apparel|garments?|footwear|shoes?|sneakers?|boots?(?![a-z])|sandals?|accessor\w*|jewell?er\w*|jewels?(?![a-z])|wear(?![a-z])|dresses?|shirts?|trousers?|(?<![a-z])pants?(?![a-z])|(?<![a-z])jeans?(?![a-z])|skirts?|jackets?|coats?(?![a-z])|lingerie|socks?|(?<![a-z])sizes?(?![a-z])|handbags?|(?<![a-z])bags?(?![a-z])|outfits?|textiles?/i;

function labelNeedsSizes(value) {
  return SIZE_LABEL_PATTERN.test(String(value || ""));
}

export function isFashionCategory(category, subcategory = "") {
  return (
    category === FASHION_CATEGORY ||
    labelNeedsSizes(category) ||
    labelNeedsSizes(subcategory)
  );
}

const normalizeQty = (raw) => {
  if (raw === null || raw === undefined || raw === "") return "1";
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0) return "1";
  return String(Math.floor(n));
};

/**
 * Normalize sizes from API → [{ size, quantity }]
 * Accepts:
 * - [{ size, quantity }]
 * - ["M","L"] (legacy)
 * - "M,L" (legacy)
 */
export function normalizeSizesFromApi(raw) {
  if (!raw) return [];

  if (Array.isArray(raw)) {
    const mapped = raw
      .map((entry) => {
        if (entry == null) return null;
        if (typeof entry === "string") {
          const size = entry.trim();
          if (!size) return null;
          return { size, quantity: "1" };
        }
        if (typeof entry === "object") {
          const size = String(entry.size ?? entry.name ?? entry.label ?? "").trim();
          if (!size) return null;
          return { size, quantity: normalizeQty(entry.quantity ?? entry.qty ?? entry.stock) };
        }
        return null;
      })
      .filter(Boolean);

    // Dedupe by size (keep first)
    const seen = new Set();
    return mapped.filter((row) => {
      const key = row.size.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  if (typeof raw === "string" && raw.trim()) {
    return raw
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean)
      .map((size) => ({ size, quantity: "1" }));
  }

  return [];
}

/**
 * Payload value for create/update.
 * Empty → [] so backend can clear on edit; non-fashion → [].
 * Shape: [{ size: "M", quantity: "20" }, ...]
 */
export function sizesForPayload(sizes, productCategory, subcategory = "") {
  if (!isFashionCategory(productCategory, subcategory)) return [];
  if (!Array.isArray(sizes) || sizes.length === 0) return [];

  const seen = new Set();
  const out = [];

  for (const entry of sizes) {
    const size = String(entry?.size ?? entry ?? "").trim();
    if (!size) continue;
    const key = size.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);

    const qtyNum = Math.max(0, Math.floor(Number(entry?.quantity ?? 0)));
    out.push({
      size,
      quantity: String(Number.isFinite(qtyNum) ? qtyNum : 0),
    });
  }

  return out;
}

export function getSelectedSizeLabels(sizes = []) {
  return (Array.isArray(sizes) ? sizes : [])
    .map((s) => (typeof s === "string" ? s : s?.size))
    .filter(Boolean);
}

/** Total units across size rows — drives Quantity Available when sizes are set. */
export function sumSizeQuantities(sizes = []) {
  if (!Array.isArray(sizes) || sizes.length === 0) return 0;
  return sizes.reduce((sum, row) => {
    const n = Math.floor(Number(row?.quantity));
    return sum + (Number.isFinite(n) && n > 0 ? n : 0);
  }, 0);
}
